import { useState, useEffect } from "react";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { useSearchParams } from "react-router-dom";
import { rpcCall, subscribe } from "../ws/client";
import { toast } from "./ui/Toast";
import { handleTransactionError, checkAndNotifyPending } from "../utils/txRecovery";
import { Button } from "./ui";
import { useNetwork } from "../hooks/useNetwork";
import { getJettonsForNetwork } from "../config/jettons";
import PoolChart from "./PoolChart";
import "../styles/EscrowBalance.scss";

interface EscrowBalanceData {
    success: boolean;
    escrow_address?: string;
    ton_balance: number;
    jetton_balance: number;
    jetton_address?: string;
    current_rate?: number;  // TON за 1 jetton
    is_initialized?: boolean; // Инициализирован ли кошелек
    error?: string;
}

interface JettonOption {
    symbol: string;
    name: string;
    address: string;
    icon: string;
}

// Форматирование чисел для удобного отображения
const formatNumber = (num: number, maxDecimals: number = 6): string => {
    if (num === 0) return "0";
    
    // Для очень маленьких чисел используем научную нотацию
    if (num < 0.000001) {
        return num.toExponential(2);
    }
    
    // Для маленьких чисел показываем больше знаков
    if (num < 0.01) {
        // Находим первую значащую цифру
        const str = num.toString();
        const match = str.match(/0\.(0*)([1-9])/);
        if (match) {
            const zeros = match[1].length;
            // Показываем 2-3 значащие цифры после первой ненулевой
            return num.toFixed(Math.min(zeros + 3, 10));
        }
    }
    
    // Для обычных чисел
    return num.toFixed(Math.min(maxDecimals, 6));
};

export default function EscrowBalance() {
    const wallet = useTonWallet();
    const [tonConnectUI] = useTonConnectUI();
    const { network, loading: networkLoading } = useNetwork();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedJetton, setSelectedJetton] = useState<JettonOption | null>(null);
    const [balance, setBalance] = useState<EscrowBalanceData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(true); // Развернуто по умолчанию на отдельной странице
    const [buyAmount, setBuyAmount] = useState("");
    const [buying, setBuying] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [pendingPurchase, setPendingPurchase] = useState<{
        jetton_amount: number;
        ton_cost: number;
        ton_tx_hash?: string;
    } | null>(null);

    // Set initial jetton when network is loaded or network changes
    useEffect(() => {
        console.log(`🔄 EscrowBalance: useEffect triggered. networkLoading=${networkLoading}, network=${network}`);
        if (!networkLoading) {
            const availableJettons = getJettonsForNetwork(network);
            console.log(`🔄 EscrowBalance: Available jettons for ${network}:`, availableJettons.map(j => ({ symbol: j.symbol, address: j.address })));
            
            // Check if there's a jetton parameter in URL
            const jettonParam = searchParams.get('jetton');
            
            if (jettonParam) {
                // Try to find jetton by address from URL parameter
                const jettonFromUrl = availableJettons.find(j => 
                    j.address.toLowerCase() === jettonParam.toLowerCase() ||
                    j.symbol.toLowerCase() === jettonParam.toLowerCase()
                );
                
                if (jettonFromUrl) {
                    console.log(`🔗 EscrowBalance: Selected jetton from URL:`, jettonFromUrl);
                    setSelectedJetton(jettonFromUrl);
                    // Remove jetton parameter from URL after selecting
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('jetton');
                    setSearchParams(newParams, { replace: true });
                    return;
                }
            }
            
            if (availableJettons.length > 0) {
                // If network changed, reset selected jetton to first available
                // or if no jetton selected yet
                const addressMatch = availableJettons.find(j => j.address === selectedJetton?.address);
                console.log(`🔄 EscrowBalance: Current selectedJetton:`, selectedJetton, "Address match:", addressMatch);
                if (!selectedJetton || !addressMatch) {
                    console.log(`🌐 Network: ${network}, selecting jetton:`, availableJettons[0]);
                    setSelectedJetton(availableJettons[0]);
                }
            }
        }
    }, [networkLoading, network, searchParams]);

    useEffect(() => {
        if (isExpanded && selectedJetton) {
            loadBalance();
        }
    }, [selectedJetton, isExpanded]);

    useEffect(() => {
        if (!isExpanded || !selectedJetton) return;
        
        loadBalance();
        
        // Subscribe to escrow balance updates
        const unsubscribe = subscribe("escrow.updated", () => {
            loadBalance();
        });
        
        return () => unsubscribe();
    }, [selectedJetton]);

    const loadBalance = async () => {
        if (!selectedJetton) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await rpcCall<
                { jetton_address?: string },
                EscrowBalanceData
            >("escrow.get_balance", {
                jetton_address: selectedJetton.address
            });

            if (response.result) {
                setBalance(response.result);
                if (!response.result.success && response.result.error) {
                    setError(response.result.error);
                }
            } else if (response.error) {
                setError(response.error.message);
            }
        } catch (err: any) {
            setError(err.message || "Ошибка загрузки баланса");
        } finally {
            setLoading(false);
        }
    };

    const handleBuyJettons = async () => {
        if (!wallet) {
            toast.warning("Подключите кошелёк для покупки");
            return;
        }

        const amount = parseFloat(buyAmount);
        if (!amount || amount <= 0) {
            toast.error("Введите корректное количество жетонов");
            return;
        }
        // Проверяем минимальную сумму в TON
        if (balance?.current_rate) {
            const tonCost = amount * balance.current_rate;
            if (tonCost < 1) {
                const minJettons = Math.ceil(1 / balance.current_rate);
                toast.error(`Минимальная покупка — 1 TON (от ${minJettons} жетонов)`);
                return;
            }
        }
        if (!balance || balance.jetton_balance < amount) {
            toast.error("Недостаточно жетонов в пуле");
            return;
        }

        setBuying(true);
        try {
            // Step 1: Get transaction for signing
            const response = await rpcCall<
                { buyer_wallet: string; jetton_address: string; jetton_amount: number },
                { 
                    success: boolean; 
                    transaction?: any;
                    ton_cost: number; 
                    jetton_amount: number;
                    current_rate: number;
                    effective_rate: number;
                    needs_signature?: boolean;
                    message: string;
                    error?: string;
                    pending_tx_id?: string;
                }
            >("escrow.buy_jettons", {
                buyer_wallet: wallet.account.address,
                jetton_address: selectedJetton!.address,
                jetton_amount: amount
            });

            if (response.result?.success && response.result.needs_signature && response.result.transaction) {
                const { ton_cost, transaction, pending_tx_id } = response.result;
                
                console.log("[Buy Jettons] Pending TX ID:", pending_tx_id);
                toast.info(`💰 Стоимость: ${ton_cost.toFixed(4)} TON\n🔐 Подтвердите транзакцию в кошельке...`);
                
                // Step 2: Send transaction via TonConnect
                let result;
                try {
                    result = await tonConnectUI.sendTransaction(transaction);
                    const boc = result.boc;
                    
                    // Get transaction hash from BOC
                    // For now, we'll wait a bit and confirm
                    toast.info("⏳ Транзакция отправлена, ожидаем подтверждения...");
                    
                    // Store pending purchase
                    setPendingPurchase({
                        jetton_amount: amount,
                        ton_cost: ton_cost
                    });
                    
                    // Wait a bit for transaction to be processed
                    await new Promise(resolve => setTimeout(resolve, 3000));
                    
                    // Step 3: Confirm purchase on backend
                    const confirmResponse = await rpcCall<
                        { 
                            buyer_wallet: string; 
                            jetton_address: string; 
                            jetton_amount: number;
                            ton_tx_hash: string;
                            pending_tx_id?: string;
                        },
                        { 
                            success: boolean; 
                            jetton_tx_hash?: string;
                            message?: string;
                            error?: string;
                        }
                    >("escrow.confirm_jetton_purchase", {
                        buyer_wallet: wallet.account.address,
                        jetton_address: selectedJetton!.address,
                        jetton_amount: amount,
                        ton_tx_hash: boc,
                        pending_tx_id: pending_tx_id
                    });
                    
                    if (confirmResponse.result?.success) {
                        toast.success(
                            `✅ ${confirmResponse.result.message}\n` +
                            `Получено: ${amount} ${selectedJetton!.symbol}\n` +
                            (confirmResponse.result.jetton_tx_hash 
                                ? `TX: ${confirmResponse.result.jetton_tx_hash.slice(0, 8)}...` 
                                : ""),
                            { duration: 5000 }
                        );
                        
                        setBuyAmount("");
                        setPendingPurchase(null);
                        await loadBalance();
                    } else {
                        const errorMsg = confirmResponse.result?.error || "Ошибка при подтверждении покупки";
                        toast.error(
                            `❌ ${errorMsg}\n\n` +
                            `⚠️ TON были отправлены, но жетоны не получены.\n` +
                            `📋 Транзакция отслеживается системой.\n` +
                            `🔄 Проверьте статус в разделе "Ожидающие транзакции" выше.`,
                            { duration: 10000 }
                        );
                    }
                    
                } catch (sendError: any) {
                    // Handle SDK errors with recovery
                    await handleTransactionError(sendError, pending_tx_id, loadBalance);
                    return;
                }
                
            } else {
                const errorMsg = response.result?.error || "Ошибка при создании транзакции";
                console.error("Buy error:", errorMsg, response);
                
                // Более понятные сообщения об ошибках
                if (errorMsg.includes("Пул пуст") || errorMsg.includes("Недостаточно")) {
                    toast.error(`⚠️ ${errorMsg}\n\nОбратитесь к администратору для пополнения пула.`);
                } else if (errorMsg.includes("Failed to check escrow balance")) {
                    toast.error("🔌 Ошибка подключения к блокчейну. Попробуйте позже.");
                } else {
                    toast.error(`❌ ${errorMsg}`);
                }
            }
        } catch (err: any) {
            console.error("Buy jettons error:", err);
            toast.error(err.message || "Ошибка при покупке");
        } finally {
            setBuying(false);
        }
    };

    const handleInitializeEscrow = async () => {
        if (!balance?.escrow_address) {
            toast.error("Адрес escrow не найден");
            return;
        }

        setInitializing(true);
        try {
            const response = await rpcCall<
                { jetton_address?: string },
                { success: boolean; message: string; tx_hash?: string; error?: string }
            >("escrow.initialize", {
                jetton_address: selectedJetton?.address
            });

            if (response.result?.success) {
                toast.success(
                    `✅ ${response.result.message}\n` +
                    (response.result.tx_hash ? `TX: ${response.result.tx_hash.slice(0, 8)}...` : "")
                );
                
                // Перезагружаем баланс через 5 секунд
                setTimeout(() => {
                    loadBalance();
                }, 5000);
            } else {
                const errorMsg = response.result?.error || response.error?.message || "Ошибка при инициализации";
                toast.error(`❌ ${errorMsg}`);
            }
        } catch (err: any) {
            console.error("Initialize escrow error:", err);
            toast.error(err.message || "Ошибка при инициализации кошелька");
        } finally {
            setInitializing(false);
        }
    };

    // Get available jettons for current network (TON excluded, only jettons for pool)
    const availableJettons = getJettonsForNetwork(network);

    return (
        <div className={`escrow-balance ${isExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="escrow-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h2>💎 Пул ликвидности</h2>
                <button className="toggle-button">
                    {isExpanded ? '▼' : '▶'}
                </button>
            </div>
            
            {isExpanded && (
                <>
                    <div className="jetton-selector">
                        <label>Выберите токен: <small style={{opacity: 0.6}}>({network})</small></label>
                        <div className="jetton-options">
                            {availableJettons.map((jetton) => (
                                <button
                                    key={jetton.symbol}
                                    className={`jetton-option ${selectedJetton?.symbol === jetton.symbol ? "active" : ""}`}
                                    onClick={() => setSelectedJetton(jetton)}
                                    disabled={networkLoading}
                                >
                                    <img src={jetton.icon} alt={jetton.symbol} />
                                    <span>{jetton.symbol}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <div className="error-message">
                            ⚠️ {error}
                        </div>
                    )}

                    {balance && balance.success && (
                        <>
                            <div className="balance-info">
                                <div className="balance-item">
                                    <span className="label">TON баланс:</span>
                                    <span className="value">{formatNumber(balance.ton_balance, 4)} TON</span>
                                </div>
                                <div className="balance-item">
                                    <span className="label">{selectedJetton.symbol} баланс:</span>
                                    <span className="value">{formatNumber(balance.jetton_balance, 2)} {selectedJetton.symbol}</span>
                                </div>
                                
                                {/* Предупреждение о неинициализированном кошельке */}
                                {balance.is_initialized === false && (
                                    <div className="error-message" style={{marginTop: "10px", padding: "12px", background: "rgba(220, 53, 69, 0.1)", border: "1px solid #dc3545", borderRadius: "8px"}}>
                                        <strong>🚨 Кошелек Escrow не активирован!</strong>
                                        <p style={{marginTop: "8px", marginBottom: "8px", fontSize: "0.9em"}}>
                                            На кошельке есть {formatNumber(balance.ton_balance, 4)} TON, но смарт-контракт не развернут.
                                            Кошелек может принимать средства, но не может их отправлять.
                                        </p>
                                        <p style={{marginTop: "8px", marginBottom: "8px", fontSize: "0.9em"}}>
                                            <strong>Как активировать:</strong><br/>
                                            1. Импортируйте seed-фразу escrow кошелька в любой TON кошелек (например, Tonkeeper)<br/>
                                            2. Отправьте любую небольшую транзакцию (например, 0.001 TON самому себе)<br/>
                                            3. Это развернёт смарт-контракт и активирует кошелек
                                        </p>
                                        {balance.escrow_address && (
                                            <div style={{marginTop: "8px", padding: "8px", background: "rgba(0,0,0,0.2)", borderRadius: "4px", wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.85em"}}>
                                                <div><strong>Адрес:</strong></div>
                                                <div style={{marginTop: "4px"}}>{balance.escrow_address}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {balance.jetton_balance <= 0 && (
                                    <div className="warning-message" style={{marginTop: "10px", padding: "10px", background: "#20091e", border: "1px solid #ffc107", borderRadius: "4px"}}>
                                        ⚠️ Пул пуст! Жетоны отсутствуют. Покупка невозможна.
                                    </div>
                                )}
                                {balance.escrow_address && (
                                    <div className="balance-item address">
                                        <span className="label">Адрес escrow:</span>
                                        <span className="value mono">{balance.escrow_address.slice(0, 8)}...{balance.escrow_address.slice(-6)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Форма покупки жетонов */}
                            {wallet && balance.jetton_balance > 0 && (
                                <div className="buy-form">
                                    <h3>Купить жетоны за TON</h3>
                                    
                                    {pendingPurchase && (
                                        <div className="pending-purchase" style={{
                                            padding: "12px",
                                            background: "rgba(255, 193, 7, 0.1)",
                                            border: "1px solid #ffc107",
                                            borderRadius: "8px",
                                            marginBottom: "15px"
                                        }}>
                                            <strong>⏳ Ожидание подтверждения</strong>
                                            <p style={{margin: "8px 0 0 0", fontSize: "0.9em"}}>
                                                Покупка {pendingPurchase.jetton_amount} {selectedJetton.symbol} за {pendingPurchase.ton_cost.toFixed(4)} TON
                                            </p>
                                        </div>
                                    )}
                                    
                                    <div className="form-group">
                                        <label>Количество {selectedJetton.symbol}:</label>
                                        <input
                                            type="number"
                                            value={buyAmount}
                                            onChange={(e) => setBuyAmount(e.target.value)}
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                            className="input-field"
                                            disabled={buying || !!pendingPurchase}
                                        />
                                        <small className="help-text">
                                            Доступно в пуле: {formatNumber(balance.jetton_balance, 2)} {selectedJetton.symbol}
                                        </small>
                                        {buyAmount && parseFloat(buyAmount) > 0 && balance.current_rate !== undefined && (
                                            <div className="price-info">
                                                <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>
                                                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap"}}>
                                                        <span>Стоимость:</span>
                                                        <strong style={{fontSize: "1.1em"}}>{formatNumber(parseFloat(buyAmount) * balance.current_rate, 4)} TON</strong>
                                                    </div>
                                                    <small style={{fontSize: "0.85em", opacity: 0.8, wordBreak: "break-all"}}>
                                                        курс: 1 {selectedJetton.symbol} ≈ {formatNumber(balance.current_rate)} TON
                                                    </small>
                                                    <small className="slippage-warning" style={{fontSize: "0.75em", fontStyle: "italic"}}>
                                                        * цена учитывает проскальзывание
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        variant="primary"
                                        block
                                        loading={buying}
                                        onClick={handleBuyJettons}
                                        disabled={!buyAmount || parseFloat(buyAmount) <= 0 || !!pendingPurchase}
                                    >
                                        💰 Купить {buyAmount || "0"} {selectedJetton.symbol}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                    
                    {/* График курса жетона */}
                    {selectedJetton && (
                        <PoolChart 
                            jettonAddress={selectedJetton.address}
                            jettonSymbol={selectedJetton.symbol}
                        />
                    )}
                    
                    <button 
                        className="refresh-button" 
                        onClick={loadBalance}
                        disabled={loading}
                    >
                        {loading ? "⏳ Загрузка..." : "🔄 Обновить"}
                    </button>
                </>
            )}
        </div>
    );
}
